import { HTMLAttributes, ReactNode, useId } from "react";
import classNames from "classnames/bind";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardLinkOverlay,
	CardMedia,
	CardTitle,
	CardTitleProps,
	CardProps,
} from "app/components/ui/Card/Card";
import {
	Carousel,
	CarouselContent,
	CarouselDots,
	CarouselItem,
} from "app/components/ui/Carousel/Carousel";
import { Image } from "app/components/ui/Image/Image";
import { Link, LinkProps } from "app/components/ui/Link/Link";
import { ArrowRightIcon } from "app/icons/navigation";
import Typography from "app/pages/.shared/Typography/Typography";
import styles from "./PrePackageCard.module.scss";

const cx = classNames.bind(styles);

const DEFAULT_PHOTO_SIZES = "(min-width: 768px) 325px, 410px";

export type PrePackagePhoto = { url: string; thumbnail?: string };

export type PrePackageCardProps = {
	/** Product page. The whole card is clickable through the title link, stretched over it. */
	to: LinkProps["to"];
	target?: LinkProps["target"];
	rel?: LinkProps["rel"];
	onClick?: LinkProps["onClick"];
	/** Destination, in the heading link. */
	title: string;
	/** Product name, announced as the description of the title link. */
	subtitle: string;
	/** Cuts the title to one line with an ellipsis. */
	truncateTitle?: boolean;
	/** Cuts the subtitle to one line with an ellipsis. */
	truncateSubtitle?: boolean;
	/** Level of the heading that holds the title link. Defaults to 3. */
	headingLevel?: CardTitleProps["headingLevel"];
	photos?: PrePackagePhoto[];
	/** Accessible name of the photo carousel. */
	photosLabel: string;
	/** Badges laid over the top left of the photo (favorite, tour...). Not shown without photos. */
	badges?: ReactNode;
	/** Rating block, resolved by the caller. */
	rating?: ReactNode;
	/** Price block (amount and duration), resolved by the caller. */
	price?: ReactNode;
	/** Miles reward block, resolved by the caller. It may hold interactive content. */
	miles?: ReactNode;
	tags?: ReactNode;
	/** Label of the call to action. Without it no button is shown and the card stays clickable. The button is decorative: assistive technologies and the keyboard reach the title link. */
	ctaLabel?: ReactNode;
	/** Renders the call to action as an icon-only button (mobile). */
	compactCta?: boolean;
	/** `sizes` attribute of the photos: tells the browser the display width to pick the image file. */
	photoSizes?: string;
	variant?: CardProps["variant"];
	padding?: CardProps["padding"];
	gap?: CardProps["gap"];
} & Omit<HTMLAttributes<HTMLElement>, "children" | "onClick" | "title">;

export const PrePackageCard = ({
	to,
	target,
	rel,
	onClick,
	title,
	subtitle,
	truncateTitle = true,
	truncateSubtitle = true,
	headingLevel,
	photos = [],
	photosLabel,
	badges,
	rating,
	price,
	miles,
	tags,
	ctaLabel,
	compactCta = false,
	photoSizes = DEFAULT_PHOTO_SIZES,
	variant = "flat",
	padding = "sm",
	gap = "md",
	className,
	...rest
}: PrePackageCardProps) => {
	const subtitleId = useId();
	const hasFooter = Boolean(price || ctaLabel || miles || tags);

	const renderPhoto = (photo: PrePackagePhoto) => (
		<Link to={to} target={target} rel={rel} onClick={onClick} tabIndex={-1} aria-hidden="true">
			<Image
				src={photo.url}
				thumbnail={photo.thumbnail}
				alt=""
				width={325}
				height={185}
				sizes={photoSizes}
			/>
		</Link>
	);

	return (
		<Card asChild variant={variant} padding={padding} gap={gap}>
			<article className={cx("pre-package-card", className)} {...rest}>
				{photos.length > 0 && (
					<CardMedia className={cx("pre-package-card__media")}>
						{photos.length > 1 ? (
							<Carousel aria-label={photosLabel}>
								<CarouselContent arrows="overlay-hover">
									{photos.map((photo, index) => (
										<CarouselItem key={index}>
											{renderPhoto(photo)}
										</CarouselItem>
									))}
								</CarouselContent>

								<CarouselDots className={cx("pre-package-card__dots")} />
							</Carousel>
						) : (
							renderPhoto(photos[0])
						)}

						{badges && <div className={cx("pre-package-card__badges")}>{badges}</div>}
					</CardMedia>
				)}

				<CardHeader>
					<CardTitle
						headingLevel={headingLevel}
						className={cx("pre-package-card__heading")}
					>
						<CardLinkOverlay
							to={to}
							target={target}
							rel={rel}
							onClick={onClick}
							aria-describedby={subtitleId}
							data-testid="pre-package-card-link"
							className={cx("pre-package-card__link")}
						>
							<Typography
								component="span"
								variant="xl"
								isBold
								className={cx("pre-package-card__title")}
								data-truncate={truncateTitle}
							>
								{title}
							</Typography>
						</CardLinkOverlay>
					</CardTitle>

					<CardDescription
						id={subtitleId}
						isBold
						className={cx("pre-package-card__subtitle")}
						data-truncate={truncateSubtitle}
					>
						{subtitle}
					</CardDescription>
				</CardHeader>

				{rating && (
					<CardContent className={cx("pre-package-card__rating")}>{rating}</CardContent>
				)}

				{hasFooter && (
					<CardFooter divider={false} className={cx("pre-package-card__footer")}>
						<div className={cx("pre-package-card__offer")}>
							<div className={cx("pre-package-card__price")}>{price}</div>

							{ctaLabel && (
								<ButtonLink
									to={to}
									target={target}
									rel={rel}
									onClick={onClick}
									variant="primary"
									size={compactCta ? "icon-md" : "md"}
									startIcon={compactCta ? <ArrowRightIcon /> : undefined}
									tabIndex={-1}
									aria-hidden="true"
									className={cx("pre-package-card__cta")}
								>
									{!compactCta && ctaLabel}
								</ButtonLink>
							)}
						</div>

						{miles && <div className={cx("pre-package-card__miles")}>{miles}</div>}
						{tags && <div className={cx("pre-package-card__tags")}>{tags}</div>}
					</CardFooter>
				)}
			</article>
		</Card>
	);
};
